/**
 * ChannelDrillPanel - Right-side slide-out panel for GA4 channel deep dive
 *
 * Shows session quality, traffic share, conversion funnel, and AI prompt
 * for a selected marketing channel on the Monarch hotel dashboard.
 *
 * Style: Solid colors only. NO gradients, NO glows, NO glassmorphism.
 * Uses bg-bg-1, border-border, font-mono for metrics.
 */

import { useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Activity,
  BarChart3,
  TrendingUp,
  MessageSquare,
} from 'lucide-react';
import { formatNumber, formatPercentRaw, formatDuration } from '@/lib/formatting';
import { useChatStore, PANEL_WIDTH_PX } from '@/stores/chatStore';
import { slideInRight, fadeBackdrop } from '@/lib/motionVariants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChannelData {
  channel: string;
  sessions: number;
  engagedSessions: number;
  engagementRate: number;
  avgTime: number;
  eventsPerSession: number;
  eventCount: number;
  keyEvents: number;
  keyEventRate: number;
}

interface ChannelDrillPanelProps {
  channel: ChannelData | null;
  open: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOTAL_SESSIONS = 87_615;
const OVERALL_AVG_TIME = 23;
const OVERALL_EVENTS_PER_SESSION = 6.36;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function engagementColor(rate: number): string {
  if (rate >= 85) return 'text-success';
  if (rate >= 70) return 'text-warning';
  return 'text-danger';
}

function engagementDotColor(rate: number): string {
  if (rate >= 85) return 'bg-success';
  if (rate >= 70) return 'bg-warning';
  return 'bg-danger';
}

function engagementBarColor(rate: number): string {
  if (rate >= 85) return 'bg-success';
  if (rate >= 70) return 'bg-warning';
  return 'bg-danger';
}

function keyEventColor(rate: number): string {
  if (rate >= 0.10) return 'text-success';
  if (rate >= 0.05) return 'text-warning';
  return 'text-danger';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionHeader({
  icon: Icon,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-text-3" />
      <h4 className="text-xs font-semibold uppercase tracking-wider text-text-3 font-display">
        {title}
      </h4>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function ChannelDrillPanel({
  channel,
  open,
  onClose,
}: ChannelDrillPanelProps) {
  const askButtonRef = useRef<HTMLButtonElement>(null);

  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const chatIsOpen = useChatStore((s) => s.isOpen);
  const chatPanelWidth = useChatStore((s) => s.panelWidth);
  const rightOffset = chatIsOpen ? PANEL_WIDTH_PX[chatPanelWidth] : 0;

  if (!open) return null;

  const data = channel;
  const trafficShare = data ? (data.sessions / TOTAL_SESSIONS) * 100 : 0;
  const timeDiff = data ? data.avgTime - OVERALL_AVG_TIME : 0;
  const eventsDiff = data ? data.eventsPerSession - OVERALL_EVENTS_PER_SESSION : 0;

  function handleAskAI() {
    if (!data) return;
    const query = `Deep dive into ${data.channel}: ${formatNumber(data.sessions)} sessions, ${formatPercentRaw(data.engagementRate)} engagement, ${data.keyEvents} key events. What's the opportunity and what should we optimize?`;

    const btn = askButtonRef.current;
    let rect: DOMRect;
    if (btn) {
      const r = btn.getBoundingClientRect();
      rect = new DOMRect(
        r.left + r.width / 2 - 1,
        r.bottom,
        2,
        0,
      );
    } else {
      rect = new DOMRect(
        window.innerWidth / 2,
        window.innerHeight - 80,
        2,
        0,
      );
    }

    useChatStore.getState().showAnalyzePrompt(query, data.channel, rect);
  }

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 z-40 bg-black/70"
        style={rightOffset > 0 ? { right: rightOffset } : undefined}
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        aria-hidden
      />

      {/* Slide-out panel */}
      <motion.div
        className="fixed top-0 z-50 h-full w-full sm:max-w-2xl bg-bg-0 border-l border-border"
        style={{ right: rightOffset }}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        role="dialog"
        aria-modal
        aria-label={data ? `${data.channel} channel drill-down` : 'Channel details'}
      >
        {/* ── Sticky Header ── */}
        <div className="sticky top-0 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-border bg-bg-1 z-10">
          <div className="flex items-center gap-3 min-w-0">
            {data && (
              <span
                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${engagementDotColor(data.engagementRate)}`}
              />
            )}
            <h2 className="text-lg font-display font-bold text-text truncate">
              {data?.channel ?? 'Channel Details'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-none bg-bg-2 border border-border hover:border-border-2 transition-colors text-text-3 hover:text-text flex-shrink-0"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Scrollable Content ── */}
        <div className="overflow-y-auto h-[calc(100%-60px)] px-4 sm:px-6 py-4 sm:py-6 space-y-6 sm:space-y-8">
          {!data ? (
            <p className="text-sm text-text-3 italic">No channel selected.</p>
          ) : (
            <>
              {/* ── Hero Metrics ── */}
              <section>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {/* Sessions */}
                  <div className="bg-bg-2 border border-border rounded-lg p-3">
                    <div className="text-[10px] text-text-3 uppercase tracking-wider mb-1">
                      Sessions
                    </div>
                    <div className="font-mono text-lg font-bold text-text">
                      {formatNumber(data.sessions)}
                    </div>
                  </div>

                  {/* Engagement Rate */}
                  <div className="bg-bg-2 border border-border rounded-lg p-3">
                    <div className="text-[10px] text-text-3 uppercase tracking-wider mb-1">
                      Engagement Rate
                    </div>
                    <div className={`font-mono text-lg font-bold ${engagementColor(data.engagementRate)}`}>
                      {formatPercentRaw(data.engagementRate)}
                    </div>
                  </div>

                  {/* Avg Engagement Time */}
                  <div className="bg-bg-2 border border-border rounded-lg p-3">
                    <div className="text-[10px] text-text-3 uppercase tracking-wider mb-1">
                      Avg Time
                    </div>
                    <div className="font-mono text-lg font-bold text-text">
                      {formatDuration(data.avgTime)}
                    </div>
                  </div>

                  {/* Key Events */}
                  <div className="bg-bg-2 border border-border rounded-lg p-3">
                    <div className="text-[10px] text-text-3 uppercase tracking-wider mb-1">
                      Key Events
                    </div>
                    <div className="font-mono text-lg font-bold text-text">
                      {formatNumber(data.keyEvents)}
                    </div>
                    <div className={`text-[10px] font-mono mt-0.5 ${keyEventColor(data.keyEventRate)}`}>
                      {formatPercentRaw(data.keyEventRate * 100, 2)} rate
                    </div>
                  </div>
                </div>
              </section>

              {/* ── Session Quality ── */}
              <section>
                <SectionHeader icon={Activity} title="Session Quality" />
                <div className="space-y-4">
                  {/* Engagement rate bar */}
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="text-text-2">Engagement Rate</span>
                      <span className={`font-mono font-semibold ${engagementColor(data.engagementRate)}`}>
                        {formatPercentRaw(data.engagementRate)}
                      </span>
                    </div>
                    <div className="h-2.5 rounded-full bg-bg-2 overflow-hidden border border-border">
                      <div
                        className={`h-full rounded-full transition-all ${engagementBarColor(data.engagementRate)}`}
                        style={{ width: `${Math.min(data.engagementRate, 100)}%` }}
                      />
                    </div>
                    <p className="text-[11px] text-text-3 mt-1.5">
                      {formatPercentRaw(data.engagementRate)} of sessions were engaged
                    </p>
                  </div>

                  {/* Avg time comparison */}
                  <div className="flex items-center justify-between px-3 py-2.5 bg-bg-2 border border-border rounded-lg">
                    <span className="text-xs text-text-2">Avg Engagement Time</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-text font-semibold">
                        {Math.round(data.avgTime)}s
                      </span>
                      <span className="text-[10px] text-text-3">vs</span>
                      <span className="font-mono text-[11px] text-text-3">23s avg</span>
                      <span
                        className={`text-xs font-mono font-semibold ${
                          timeDiff >= 0 ? 'text-success' : 'text-danger'
                        }`}
                      >
                        {timeDiff >= 0 ? '+' : ''}{Math.round(timeDiff)}s
                      </span>
                    </div>
                  </div>

                  {/* Events per session comparison */}
                  <div className="flex items-center justify-between px-3 py-2.5 bg-bg-2 border border-border rounded-lg">
                    <span className="text-xs text-text-2">Events / Session</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-text font-semibold">
                        {data.eventsPerSession.toFixed(2)}
                      </span>
                      <span className="text-[10px] text-text-3">vs</span>
                      <span className="font-mono text-[11px] text-text-3">6.36 avg</span>
                      <span
                        className={`text-xs font-mono font-semibold ${
                          eventsDiff >= 0 ? 'text-success' : 'text-danger'
                        }`}
                      >
                        {eventsDiff >= 0 ? '+' : ''}{eventsDiff.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* ── Traffic Share ── */}
              <section>
                <SectionHeader icon={BarChart3} title="Traffic Share" />
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-2">
                      Share of total sessions ({formatNumber(TOTAL_SESSIONS)})
                    </span>
                    <span className="font-mono font-semibold text-text">
                      {formatNumber(data.sessions)}
                    </span>
                  </div>
                  <div className="h-3 rounded-full bg-bg-2 overflow-hidden border border-border">
                    <div
                      className="h-full rounded-full bg-monarch-gold transition-all"
                      style={{ width: `${Math.min(trafficShare, 100)}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-text-3">
                    {trafficShare.toFixed(1)}% of total traffic
                  </p>
                </div>
              </section>

              {/* ── Conversion Funnel ── */}
              <section>
                <SectionHeader icon={TrendingUp} title="Conversion Funnel" />
                <div className="space-y-3">
                  {/* Key events count */}
                  <div className="flex items-center justify-between px-3 py-2.5 bg-bg-2 border border-border rounded-lg">
                    <span className="text-xs text-text-2">Key Events</span>
                    <span className="font-mono text-sm text-text font-semibold">
                      {formatNumber(data.keyEvents)}
                    </span>
                  </div>

                  {/* Key event rate */}
                  <div className="flex items-center justify-between px-3 py-2.5 bg-bg-2 border border-border rounded-lg">
                    <span className="text-xs text-text-2">Key Event Rate</span>
                    <span className={`font-mono text-sm font-semibold ${keyEventColor(data.keyEventRate)}`}>
                      {formatPercentRaw(data.keyEventRate * 100, 2)}
                    </span>
                  </div>

                  {/* Detail */}
                  <p className="text-[11px] text-text-3">
                    {formatNumber(data.keyEvents)} form submissions from{' '}
                    {formatNumber(data.sessions)} sessions
                  </p>
                </div>
              </section>

              {/* ── Ask AI ── */}
              <section className="pb-4">
                <button
                  ref={askButtonRef}
                  onClick={handleAskAI}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-bg-2 border border-border rounded-lg hover:border-monarch-gold transition-colors text-text-2 hover:text-monarch-gold"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm font-medium">Ask AI about this channel</span>
                </button>
              </section>
            </>
          )}
        </div>
      </motion.div>
    </>
  );
}
